import { Checkbox, FormControlLabel } from "@mui/material";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { ToastContainer } from "react-toastify";
import api from "../../../../../features/auth/axiosConfig";
import EditMenu from "../../EditMenu";
import RoleSelect from "../../RoleSelect";
import LoadingScreen from "../../../../components/LoadingScreen";
import { filters } from "../utils/userOperations";
import { menu, handleAddCardClick } from "../utils/userHelpers";
import { UserContent } from "../edit-user/component/types";
import { handleCheckboxToggle } from "../utils/useHandlers";
import UserTable from "./UserTable";
import { handleSearch } from "../utils/useSearchValidation";
import CommonButtons from "src/shared/components/CommonButtons";
import { useUserTheme } from "src/shared/utils/useUserTheme";
import { PageTitle } from "src/shared/PageTitle";
import { pageTitle } from "src/shared/utils/brand";
import DeleteConfirmation, { DeleteConfirmationHandle } from "../components/DeleteConfirmation";
import EmployeeModal from "src/business-types/grocery/layouts/setting/employee/AddEmployee";
import { User as EmployeeInterface } from "src/shared/services/interfacev2";
import { enrichUserData, enrichUsersData } from "src/shared/utils/userUtils";
import { getUser } from "src/shared/services/servicesv2";

function User() {
  const [employeeData, setEmployeeData] = useState<EmployeeInterface[]>([]);
  const [originalEmployeeData, setOriginalEmployeeData] = useState<EmployeeInterface[]>([]);
  const [, setUserContent] = useState<UserContent[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredData, setFilteredData] = useState<EmployeeInterface[]>([]);
  const [isEditMode, setIsEditMode] = useState<EmployeeInterface | null>(null);
  const [showDeletedUsers, setShowDeletedUsers] = useState<boolean>(false);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [, setPage] = useState(0);
  const [showRoleSelect, setShowRoleSelect] = useState(false);
  const [isBranchSelected, setIsBranchSelected] = useState(false);
  const [isListLoading, setIsListLoading] = useState<boolean>(false);
  const [isEditLoading, setIsEditLoading] = useState<boolean>(false);
  
  const [, setIsCheckboxClicked] = useState(false);
  const deleteModalRef = useRef<DeleteConfirmationHandle>(null);
  const [employee, setEmployee] = useState<boolean>(false);

  const isFetchingRef = useRef<boolean>(false);
  const isMountedRef = useRef<boolean>(false);

  const { fontClass, borderHighlight, sidebarThemeClass } = useUserTheme();

  const refreshUserData = useCallback(() => {
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsListLoading(true);

    filters(
      showDeletedUsers,
      api,
      setUserContent,
      (data: React.SetStateAction<EmployeeInterface[]>) => {
        const dataArray = Array.isArray(data) ? data : [];
        const enrichedData = enrichUsersData(dataArray);
        setEmployeeData(enrichedData);
        setFilteredData(enrichedData);
        setOriginalEmployeeData(enrichedData);
        setIsListLoading(false);
        isFetchingRef.current = false;
      },
      setFilteredData,
      (loadingState: boolean) => {
        setIsListLoading(loadingState);
        if (!loadingState) isFetchingRef.current = false;
      },
    );
  }, [showDeletedUsers]);

  // Initial mount trigger with StrictMode double-call guard
  useEffect(() => {
    document.title = pageTitle("Users › Settings");

    if (!isMountedRef.current) {
      isMountedRef.current = true;
      refreshUserData();
    }
  }, [refreshUserData]);

  const handleAddCard = (id: string) => {
    handleAddCardClick(id, setSelectedUserId, setShowRoleSelect, setIsBranchSelected);
  };

  const handleEdit = (id: string) => {
    if (!id) {
      console.error("No ID provided for edit");
      return;
    }

    // Set edit loading state
    setIsEditLoading(true);

    // Try to find user in current data
    let editUser = employeeData.find((i) => i._id?.$oid === id) || 
                   originalEmployeeData.find((i) => i._id?.$oid === id);

    if (!editUser) {
      // If user not found in current data, fetch from API
      getUser(id)
        .then((response) => {
          let userData = response.data;

          if (userData.result && Array.isArray(userData.result)) {
            userData = userData.result[0];
          }

          if (!userData) {
            console.error("No user data found in response");
            setIsEditLoading(false);
            return;
          }

          if (!userData._id) {
            userData._id = { $oid: id };
          }

          const enrichedData = enrichUserData(userData);

          // Add to original data for future reference
          setOriginalEmployeeData((prev) => {
            const exists = prev.some((u) => u._id?.$oid === id);
            if (exists) {
              return prev.map((u) => (u._id?.$oid === id ? enrichedData : u));
            }
            return [...prev, enrichedData];
          });

          setIsEditMode(enrichedData);
          setEmployee(true);
          setIsEditLoading(false);
        })
        .catch((error) => {
          console.error("Failed to fetch user:", error);
          setIsEditLoading(false);
        });
      return;
    }

    // User found in current data
    const enrichedData = enrichUserData(editUser);
    setIsEditMode(enrichedData);
    setEmployee(true);
    setIsEditLoading(false);
  };

  const handleCheckboxClick = () => {
    // Reset search term when toggling deleted users
    setSearchTerm("");
    
    handleCheckboxToggle(
      showDeletedUsers, 
      setShowDeletedUsers, 
      setIsCheckboxClicked, 
      setSearchTerm, 
      (value: boolean) => {
        setIsListLoading(true);
        isFetchingRef.current = false;
        filters(
          value, 
          api, 
          setUserContent, 
          (data: React.SetStateAction<EmployeeInterface[]>) => {
            const dataArray = Array.isArray(data) ? data : [];
            const enrichedData = enrichUsersData(dataArray);
            setEmployeeData(enrichedData);
            setFilteredData(enrichedData);
            setOriginalEmployeeData(enrichedData);
            setIsListLoading(false);
            isFetchingRef.current = false;
          }, 
          setFilteredData, 
          (loadingState: boolean) => {
            setIsListLoading(loadingState);
            if (!loadingState) isFetchingRef.current = false;
          }
        );
      }
    );
  };

  const handleDeleteUserClick = (id: string | number, username?: string) => {
    if (!id) return;
    deleteModalRef.current?.openModal(id, username ?? "");
  };

  const handleRoleSelectClose = () => {
    setShowRoleSelect(false);
    setSelectedUserId("");
    setIsBranchSelected(false);
  };

  return (
    <div className="p-1">
      <div className={`flex flex-col w-full h-[91vh] shadow-inner rounded-lg items-center ${sidebarThemeClass} overflow-hidden`}>
        <PageTitle title="- Settings › User" />
        <EditMenu editmenu={menu} />
        <div className="customer-toolbar flex items-center justify-end gap-4 p-2">
          <div className="grid grid-cols-1 gap-y-3 sm:grid-cols-[auto_1fr_auto] sm:gap-x-4 lg:flex lg:flex-nowrap lg:items-center lg:gap-x-4 lg:justify-end w-full lg:w-[95vw] xl:w-[88vw] min-h-[60px] px-4 pt-7 pb-3 rounded-lg">
            <div className="flex items-center" id="view-deleted-users">
              <FormControlLabel 
                control={<Checkbox checked={showDeletedUsers} />} 
                onClick={handleCheckboxClick} 
                label="View deleted users" 
                id="deleted-users-checkbox" 
              />
            </div>
            <div className="user-search-input-wrap">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearch(e, employeeData, setFilteredData, setSearchTerm, setPage)}
                placeholder="Search by Name or Phone number"
                className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 ${borderHighlight} ${fontClass} text-sm text-black`}
              />
            </div>
            <div className="add-user-btn">
              <CommonButtons className="w-44 h-10" label="ADD USER" onClick={() => setEmployee(true)} />
            </div>
          </div>
        </div>
        <UserTable
          filteredData={filteredData}
          onEditUser={handleEdit}
          onAddCardClick={handleAddCard}
          isBranchSelected={isBranchSelected}
          onDeleteClick={handleDeleteUserClick}
          showDeletedUsers={showDeletedUsers}
        />
      </div>

      <EmployeeModal
        key={isEditMode?._id?.$oid || "new"}
        onClose={() => {
          setEmployee(false);
          setIsEditMode(null);
        }}
        open={employee}
        editData={isEditMode}
        onSuccess={() => {
          isFetchingRef.current = false;
          refreshUserData();
        }}
      />

      <DeleteConfirmation
        ref={deleteModalRef}
        onDeleteSuccess={() => {
          isFetchingRef.current = false;
          refreshUserData();
        }}
      />

      {showRoleSelect && <RoleSelect id={selectedUserId} onClose={handleRoleSelectClose} />}
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick pauseOnHover />

      {/* Separate loading screens */}
      {isListLoading && <LoadingScreen message="Please wait, loading users..." />}
      {isEditLoading && <LoadingScreen message="Please wait, loading user details..." />}
    </div>
  );
}

export default User;